import { useEffect, useState } from 'react';

type Reaction = {
  emoji: string;
  label: string;
  key: string;
  color: string;
};

const REACTIONS: Reaction[] = [
  { emoji: '🔥', label: 'Harika', key: 'fire', color: '#FF6B35' },
  { emoji: '👍', label: 'Beğendim', key: 'like', color: '#00D9FF' },
  { emoji: '🤯', label: 'İnanılmaz', key: 'mind_blown', color: '#A78BFA' },
  { emoji: '😮', label: 'Şaşırdım', key: 'wow', color: '#FFB347' },
  { emoji: '💡', label: 'Bilgilendirici', key: 'insightful', color: '#00FF88' },
  { emoji: '❤️', label: 'Sevdim', key: 'love', color: '#FF6B9D' },
];

const STORAGE_PREFIX = 'reactions_';

interface ReactionBarProps {
  newsId: string;
}

export default function ReactionBar({ newsId }: ReactionBarProps) {
  const storageKey = `${STORAGE_PREFIX}${newsId}`;
  const votedKey = `${STORAGE_PREFIX}voted_${newsId}`;

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [voted, setVoted] = useState<string | null>(null);
  const [animating, setAnimating] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      setCounts(JSON.parse(stored));
    } else {
      // Seed with realistic initial counts
      const seed: Record<string, number> = {};
      REACTIONS.forEach(r => {
        seed[r.key] = Math.floor(Math.random() * 80) + 5;
      });
      setCounts(seed);
      localStorage.setItem(storageKey, JSON.stringify(seed));
    }
    const v = localStorage.getItem(votedKey);
    if (v) setVoted(v);
  }, [newsId, storageKey, votedKey]);

  const handleReact = (key: string) => {
    if (voted === key) {
      // Undo vote
      const updated = { ...counts, [key]: Math.max(0, (counts[key] || 0) - 1) };
      setCounts(updated);
      localStorage.setItem(storageKey, JSON.stringify(updated));
      localStorage.removeItem(votedKey);
      setVoted(null);
      return;
    }

    const updated = { ...counts };
    if (voted) {
      updated[voted] = Math.max(0, (updated[voted] || 0) - 1);
    }
    updated[key] = (updated[key] || 0) + 1;

    setCounts(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
    localStorage.setItem(votedKey, key);
    setVoted(key);
    setAnimating(key);
    setTimeout(() => setAnimating(null), 600);
  };

  const totalReactions = Object.values(counts).reduce((a, b) => a + b, 0);
  const topReaction = REACTIONS.reduce((top, r) =>
    (counts[r.key] || 0) > (counts[top.key] || 0) ? r : top, REACTIONS[0]);

  const displayedReactions = showAll ? REACTIONS : REACTIONS.slice(0, 4);

  return (
    <div className="mt-10 pt-8 border-t border-white/10">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-2xl">{topReaction.emoji}</span>
            <div>
              <div className="text-white font-bold text-sm">Bu habere ne düşünüyorsunuz?</div>
              <div className="text-white/30 text-xs">
                {totalReactions.toLocaleString()} toplam tepki
              </div>
            </div>
          </div>
        </div>
        {voted && (
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{
              backgroundColor: `${REACTIONS.find(r => r.key === voted)?.color}15`,
              color: REACTIONS.find(r => r.key === voted)?.color,
            }}
          >
            <i className="ri-check-line" />
            Tepkiniz kaydedildi
          </div>
        )}
      </div>

      {/* Reaction Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {displayedReactions.map(reaction => {
          const count = counts[reaction.key] || 0;
          const isVoted = voted === reaction.key;
          const isAnimating = animating === reaction.key;
          const pct = totalReactions > 0 ? Math.round((count / totalReactions) * 100) : 0;

          return (
            <button
              key={reaction.key}
              onClick={() => handleReact(reaction.key)}
              title={reaction.label}
              className={`group relative flex items-center gap-2 px-4 py-2.5 rounded-2xl border transition-all duration-200 cursor-pointer overflow-hidden ${
                isVoted
                  ? 'border-transparent scale-105'
                  : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20 hover:scale-105'
              } ${isAnimating ? 'scale-110' : ''}`}
              style={isVoted ? {
                backgroundColor: `${reaction.color}15`,
                borderColor: `${reaction.color}40`,
              } : {}}
            >
              {/* Background fill based on percentage */}
              {isVoted && (
                <div
                  className="absolute inset-0 opacity-10 transition-all duration-500"
                  style={{ backgroundColor: reaction.color, width: `${pct}%` }}
                />
              )}

              <span
                className={`text-xl transition-transform duration-200 ${isAnimating ? 'scale-125' : 'group-hover:scale-110'}`}
              >
                {reaction.emoji}
              </span>
              <div className="flex flex-col items-start relative z-10">
                <span
                  className="text-xs font-bold leading-none"
                  style={{ color: isVoted ? reaction.color : 'rgba(255,255,255,0.7)' }}
                >
                  {reaction.label}
                </span>
                <span
                  className="text-[10px] font-semibold mt-0.5"
                  style={{ color: isVoted ? reaction.color : 'rgba(255,255,255,0.3)' }}
                >
                  {count.toLocaleString()}
                  {pct > 0 && <span className="ml-1 opacity-60">· %{pct}</span>}
                </span>
              </div>
            </button>
          );
        })}

        {!showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-white/40 hover:text-white/70 text-xs font-semibold transition-all cursor-pointer"
          >
            <i className="ri-add-line" />
            Daha fazla
          </button>
        )}
      </div>

      {/* Reaction summary bar */}
      <div className="flex items-center gap-1 h-1.5 rounded-full overflow-hidden bg-white/5">
        {REACTIONS.map(r => {
          const pct = totalReactions > 0 ? (counts[r.key] || 0) / totalReactions * 100 : 0;
          return pct > 0 ? (
            <div
              key={r.key}
              className="h-full transition-all duration-500 rounded-full"
              style={{ width: `${pct}%`, backgroundColor: r.color }}
              title={`${r.label}: %${Math.round(pct)}`}
            />
          ) : null;
        })}
      </div>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {REACTIONS.filter(r => (counts[r.key] || 0) > 0).slice(0, 4).map(r => (
          <span key={r.key} className="text-[10px] text-white/30 flex items-center gap-1">
            <span>{r.emoji}</span>
            <span style={{ color: r.color }}>{Math.round(totalReactions > 0 ? (counts[r.key] || 0) / totalReactions * 100 : 0)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}
